import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch-manipulation",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-primary-foreground hover:scale-105 shadow-medium hover:shadow-elevated transition-all duration-300 hover:brightness-110",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-all duration-200",
        outline: "border border-input bg-gradient-card backdrop-blur-glass hover:bg-accent hover:text-accent-foreground hover:scale-105 transition-all duration-300",
        secondary: "bg-gradient-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-105 transition-all duration-300",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:scale-105 transition-all duration-200",
        link: "text-primary underline-offset-4 hover:underline hover:scale-105 transition-all duration-200",
        success: "bg-gradient-success text-accent-foreground hover:scale-105 shadow-medium hover:shadow-elevated transition-all duration-300 hover:brightness-110",
        hero: "bg-gradient-hero text-primary-foreground hover:scale-105 shadow-elevated hover:shadow-strong transition-all duration-300 text-base hover:brightness-110",
        glass: "bg-gradient-glass backdrop-blur-glass border border-white/20 text-foreground hover:bg-white/10 hover:scale-105 transition-all duration-300",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
